import { useCallback, useRef } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { isTap } from '../../application/motion/useCardGestures';
import useTypewriter from '../../application/motion/useTypewriter';
import { carName, carNameParts, carTagline } from '../../domain/cars/carCatalogue';
import { heroTake } from '../../domain/cars/carSchema';
import { artSource } from './carArt';

/**
 * One seat in the deck: a car as the card §12 draws it.
 *
 * The artwork bleeds to the TOP EDGE of the screen. That is the whole reason the
 * car detail route is `immersive` in the route table — the shell drops its
 * gutters, the header and the tab bar are gone, and the first thing under the
 * status bar is a Countach. Anything between the two would be furniture in front
 * of the one object the reader opened.
 *
 * THE NAME IS PRINTED ON THE PICTURE, top-left, the way the 8-bit car screens
 * this product is a catalogue of drew it (SPEC-004 PR-4.8): the year and the
 * marque in small tracked capitals, the model under them in the display face,
 * and the tagline under that. It sat on the paper BELOW the artwork until the
 * screen was composed to the reference, and moving it is most of what makes the
 * panel read as that screen rather than as a poster with a caption.
 *
 * The lockup takes no pointer events. The artwork is a control now (SPEC-012
 * D-14) and it is the whole panel; a heading laid over it that swallowed the
 * press would put a dead patch exactly where the name draws the eye.
 *
 * THE FOUR FIGURES ARE NOT ON THIS CARD. They were, and they moved out to the
 * deck itself (`CarDeck`) so that one plate outlives a deal: a plate owned by a
 * seat is rebuilt when the seat behind is promoted, and a figure that is rebuilt
 * cannot roll from the last car's number to this one's — it can only appear
 * already showing the answer. The card is the ART PANEL and its lockup; the
 * instruments belong to the page.
 *
 * THE SEAT'S ROLE DECIDES WHAT IS DRAWN, and the rules are not cosmetic:
 *
 *   PEEK draws no picture at all. It is a few pixels of card edge saying there
 *        is a deck here, and a fourth decoded 1440px bitmap for that is a real
 *        cost with nothing on the other side.
 *   ONLY THE FRONT CARD PRINTS WORDS. The seats behind carry their picture and
 *        an empty paper box of exactly the right height. Two reasons: a heading
 *        per seat would put four `<h1>`s in one document, and a fully populated
 *        card behind a fully populated card means a step replaces one finished
 *        card with another almost exactly like it — which is what reads as a
 *        flicker rather than as a deal.
 *
 * The empty box is not an oversight: it is what keeps the picture in the SAME
 * place when a seat is promoted. A card behind whose paper collapsed would draw
 * its artwork taller, and the deal would end with the picture jumping.
 *
 * TAPPING THE ARTWORK OPENS IT FULL SCREEN (SPEC-012 D-14), and the button that
 * does it is drawn for EVERY seat rather than only the front one — the seats
 * behind are `inert`, so they cannot be pressed anyway, and a structure that
 * changed shape on promotion would tear the live `<img>` down and re-attach its
 * bitmap on every deal, which is exactly the repaint the seating exists to
 * avoid.
 */
/**
 * How many characters of model name still fit at display size.
 *
 * Twelve, which is one line of the widest the panel draws at 320px — "CTR
 * Yellowbird" is fourteen and already wraps, and a wrapped display line costs
 * the picture forty pixels. Deliberately generous rather than exact: the step
 * down is to the title size, which is still the largest type on the screen, so
 * a name that did not strictly need it loses nothing worth measuring.
 */
const LONG_MODEL = 12;

export default function CarDeckCard({ car, role = 'front', onOpen }) {
    const t = useTranslate();
    const isFront = role === 'front';
    // Where the press landed, so a click at the end of a DRAG can be told from a
    // click at the end of a TAP. Both arrive here: the deck's gesture watches
    // pointers and prevents nothing, so a stroke that dealt the next car still
    // ends in a click on the control it started on.
    const pressRef = useRef(null);
    const onPress = useCallback((event) => {
        pressRef.current = { x: event.clientX, y: event.clientY };
    }, []);
    const onOpenClick = useCallback((event) => {
        const press = pressRef.current;

        pressRef.current = null;

        // No press at all is a click the keyboard synthesised, which is a
        // deliberate activation and never a drag.
        if (press && !isTap({ dx: event.clientX - press.x, dy: event.clientY - press.y })) {
            return;
        }

        // `heroTake`, NOT the take the card is drawing. The viewer holds the
        // pictures a reader browses and the window-down shot is deliberately not
        // one of them (`carSchema.SELECT_TAKE`), so opening on it would ask for
        // a page that is not in the rail — and `CarViewer` would land them on
        // the first slide with no way to tell why. The hero is the same car in
        // the same view, and it IS in there.
        onOpen?.(heroTake(car));
    }, [car, onOpen]);
    // THE SIDE PROFILE, always. The card drew the window-down shot where a car
    // had one — the reasoning being that this route is where a car is CHOSEN, so
    // it should be the one with somebody sitting in it — and the silhouette is
    // what this page is actually for. A three-quarter view with the glass down
    // is a nice picture of a car; a side profile is the shape a reader
    // recognises from across a room, and it is the shape the deck deals, the
    // share card composes and the garage grid contrasts against.
    //
    // `heroTake` still falls back to the front view for a car delivered without
    // a side profile — the Testarossa is one — so the ladder that kept this
    // screen from rendering empty is unchanged.
    const art = role === 'peek' ? null : artSource(car, heroTake(car));
    const tagline = carTagline(car);
    // The one line on this card that is neither a measurement nor a name, so it
    // is the one line that arrives the way a sentence does (SPEC-004 D-62).
    const { shown: typed, isTyping } = useTypewriter(isFront ? tagline : null);
    // The marque and the model as two lines, taken off the ONE name the prose
    // carries rather than read back off the record's own `model` field — see
    // `carNameParts` for why those two are not the same string.
    const { marque, model } = carNameParts(car);

    return (
        <div className="deck-card">
            {/* The stage declares itself a gesture surface. `isSidewaysClaimed`
                turns the deck's swipe away from any control it lands on, which
                is right everywhere else on this screen and would be fatal here —
                the artwork is most of the card, and losing the swipe on it to
                gain a tap would be a bad trade. A control inside `grab` is the
                escape hatch that already exists for exactly this. */}
            <div className="deck-card__stage" data-gesture="grab">
                {/* FIRST in the stage, and in the flow rather than laid over
                    the artwork. It was absolute, which put the tagline across
                    the middle of the car on every record whose name ran to a
                    second line — a 3:1 side profile `contain`ed in a 4:3 box
                    fills exactly the band the third and fourth lines land in.
                    Tuning `object-position` would have moved the collision
                    rather than removed it, because how many lines the lockup
                    takes is a fact about the CONTENT.
                    
                    In flow, the two share the panel by construction: the words
                    take what they need and the picture takes the rest. Drawn
                    only on the card in hand — four `<h1>`s in one document is
                    what drawing it on every seat would mean. */}
                {!isFront ? (
                    /* THE BOX WITHOUT THE WORDS, on every seat behind. The
                       artwork is a flex child that takes whatever the lockup
                       leaves, so a seat with no lockup gives its picture the
                       whole panel — and promoting it mounted the lockup, shrank
                       the picture's box and jumped the car into its final
                       position. That reflow is the flicker.

                       An empty box of the same height is what the paper below
                       has always done for the same reason. */
                    <div className="deck-card__lockup" aria-hidden="true" />
                ) : (
                    <div className="deck-card__lockup">
                        <p className="deck-card__year figure">{car.year}</p>
                        {/* The document's heading for this route: a shared link
                            opens straight onto this card with no garage in front
                            of it, so the car's name is the page's name.

                            ONE heading holding two spans rather than two
                            elements, because it is one name — the marque set
                            small over the model is typography, and a screen
                            reader that met them as two strings would announce
                            the make and the model as separate headings. The
                            space between them is real and load-bearing: it is
                            what keeps this element's text "Ferrari F40". */}
                        {/* A LONG MODEL IS SET SMALLER, and the threshold is
                            counted rather than eyeballed. "Countach 25th
                            Anniversary" takes three lines at display size and
                            leaves the panel with forty-three pixels for the car
                            — the name eating the picture it is supposed to
                            label. CSS cannot ask how long a string is, and a
                            fluid size cannot either: container units scale with
                            the WIDTH of the box, and the problem here is the
                            length of the word. So the count is taken where the
                            string is, and the stylesheet is told which of two
                            sizes to use. */}
                        <h1 className={`deck-card__name${model.length > LONG_MODEL ? ' deck-card__name--long' : ''}`}>
                            {marque ? (
                                <>
                                    <span className="deck-card__marque">{marque}</span>
                                    {' '}
                                </>
                            ) : null}
                            <span className="deck-card__model">{model}</span>
                        </h1>
                        {tagline ? (
                            /* THE WHOLE SENTENCE IS IN THE DOCUMENT and only the
                               drawing is partial. A reader listening to this
                               card gets the line once, complete, the moment it
                               is rendered — a growing fragment announced
                               character by character would be the effect
                               inflicted on the one reader it cannot possibly be
                               for. The caret is a pseudo-element, so it is not
                               text either. */
                            <p className="deck-card__tagline" data-typing={isTyping ? '' : undefined}>
                                <span className="visually-hidden">{tagline}</span>
                                <span aria-hidden="true">{typed}</span>
                            </p>
                        ) : null}
                    </div>
                )}

                {art ? (
                    <button
                        type="button"
                        className="deck-card__open"
                        onPointerDown={onPress}
                        onClick={onOpenClick}
                        aria-label={t('cars.viewer.open', { car: carName(car) })}
                        /* Only the front card is reachable at all — the seats
                           behind it are `inert` — but a control that is drawn
                           and does nothing is worse than one that is not
                           offered, so the ones behind say so. */
                        tabIndex={isFront ? undefined : -1}
                    >
                        <img
                            className="deck-card__art"
                            src={art.src}
                            srcSet={art.srcSet}
                            sizes="100vw"
                            /* Decorative: the name is printed directly beneath
                               it, and a shared link opens on this card — so the
                               heading is the thing that has to speak, not the
                               picture. The button around it carries the label. */
                            alt=""
                            /* Every seated card is fetched EAGERLY, including
                               the ones off screen. That is the point of seating
                               them: a swipe reveals a picture that is already
                               decoded, and a lazily-loaded reveal would be a
                               blank card for the length of the deal. */
                            loading="eager"
                            fetchPriority={isFront ? 'high' : 'low'}
                            decoding="async"
                            /* A sideways stroke across the artwork is the deck's
                               swipe, and an `<img>` is a drag source in every
                               engine: the native drag fires on the second
                               pointer move, cancels the pointer sequence and
                               takes the deal with it. That was already true
                               before the picture became a control — the deck is
                               dragged by a finger, and a finger never starts one
                               — but a mouse does, and now the same stroke is
                               being told apart from a tap (SPEC-012 D-14). */
                            draggable={false}
                        />
                    </button>
                ) : null}

                <span className={`rarity-ribbon rarity-ribbon--${car.rarity} deck-card__rarity`}>
                    {t(`cars.rarity.${car.rarity}`)}
                </span>

            </div>

        </div>
    );
}
